import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Slide,
  Button
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from "react-router-dom";
import { motion } from 'framer-motion';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import { styled, keyframes } from '@mui/system';
import ScrollToTopButton from '../Button/ScrollToTopButton';
import DeleteIcon from '@mui/icons-material/Delete';
import * as XLSX from 'xlsx';


const slideInFromLeft = keyframes`
  0% {
    transform: translateX(-20px);
    opacity: 0;
  }
  100% {
    transform: translateX(0);
    opacity: 1;
  }
`;

const AnimatedTypography = styled(Typography)(({ theme }) => ({
  animation: `${slideInFromLeft} 0.5s ease-out`,
}));

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const currentYear = new Date().getFullYear();
const yearOptions = Array.from({ length: 5 }, (_, i) => currentYear - i);


const CombinedTable = () => {
  const [loading, setLoading] = useState(true);
  const [combinedData, setCombinedData] = useState([]);
  const [user, setUser] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [filteredData, setFilteredData] = useState([]);
  const navigate = useNavigate();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [selectedYear, setSelectedYear] = useState('');

  const toggleSearch = () => {
    setIsSearchOpen(!isSearchOpen);
  };


  const months = [
    { value: '01', label: 'January' },
    { value: '02', label: 'February' },
    { value: '03', label: 'March' },
    { value: '04', label: 'April' },
    { value: '05', label: 'May' },
    { value: '06', label: 'June' },
    { value: '07', label: 'July' },
    { value: '08', label: 'August' },
    { value: '09', label: 'September' },
    { value: '10', label: 'October' },
    { value: '11', label: 'November' },
    { value: '12', label: 'December' },
  ];

  useEffect(() => {
    const fetchProfile = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        navigate("/");
        return;
      }

      try {
        const response = await axios.get("https://admin.bizstor.lk/public/api/profile", {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUser(response.data);
      } catch (error) {
        if (error.response && error.response.status === 401) {
          localStorage.removeItem("token");
          navigate("/");
        }
      }
    };

    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

      const fetchWithRetry = async (url, retries = 3, delayMs = 1000) => {
        for (let i = 0; i < retries; i++) {
          try {
            const response = await axios.get(url);
            return response;
          } catch (error) {
            if (error.response?.status === 429) {
              const waitTime = delayMs * Math.pow(2, i);
              await delay(waitTime);
              continue;
            }
            if (i === retries - 1) throw error;
            await delay(delayMs);
          }
        }
      };

      try {
        const expensesResponse = await fetchWithRetry('https://admin.bizstor.lk/public/api/get-expense-data');
        await delay(500);

        const ordersResponse = await fetchWithRetry('https://admin.bizstor.lk/public/api/get-data');
        await delay(500);

        const expensespaymentResponse = await fetchWithRetry('https://admin.bizstor.lk/public/api/get-expensespayment');
        await delay(500);

        const orderspaymentResponse = await fetchWithRetry('https://admin.bizstor.lk/public/api/get-payment-data');
        await delay(500);

        const investmentpaymentResponse = await fetchWithRetry('https://admin.bizstor.lk/public/api/get-investment');
        await delay(500);

        const returnpaymentResponse = await fetchWithRetry('https://admin.bizstor.lk/public/api/get-damage-data');

        if (user.id) {
          const userExpenses = expensesResponse.data
            .filter(expense =>
              expense.usertoken?.toLowerCase() === user.id.toString().toLowerCase() &&
              expense.paymenttype === 'Paid'
            )
            .map(expense => {
              const totalAmount = expense.items.reduce((sum, item) => sum + Number(item.amount), 0);
              return {
                itemCategory: [...new Set(expense.items.map(i => i.category))].join(', '),
                credit: totalAmount,
                createdAt: expense.created_at,
                isExpense: true,
                fullname: expense.fullname,
                type: 'expense',
                id: expense.pay_id,
              };
            });

          const userOrders = ordersResponse.data
            .filter(order =>
              order.usertoken.toLowerCase() === user.id.toString().toLowerCase() &&
              order.term === 'due on receipt'
            )
            .map(order => {
              let orderTotal = 0;
              order.items.forEach(item => {
                const itemPrice = Number(item.rate) * Number(item.quantity);
                const itemTax = Number(item.tax) || 0;
                const itemTotalWithTax = itemPrice + (itemPrice * (itemTax / 100));
                orderTotal += itemTotalWithTax;
              });

              const discountPercentage = Number(order.discount) || 0;
              const discountAmount = (orderTotal * discountPercentage) / 100;
              const finalTotal = orderTotal - discountAmount;

              return {
                itemCategory: 'Accounts Receivable',
                debit: finalTotal || 0,
                createdAt: order.created_at,
                fullname: order.fullname || "Direct Customer",
                isExpense: false,
                type: 'order',
                id: order.pay_id,
              };
            });

          const userOrders2 = ordersResponse.data
            .filter(order =>
              order.usertoken.toLowerCase() === user.id.toString().toLowerCase() &&
              order.term !== 'due on receipt' &&
              order.payamount !== null
            )
            .map(order => ({
              itemCategory: 'Accounts Receivable',
              debit: Number(order.payamount),
              createdAt: order.created_at,
              fullname: order.fullname || "Direct Customer",
              isExpense: false,
              type: 'order',
              id: order.pay_id,
            }));

          const expensesPayments = expensespaymentResponse.data
            .filter(payment => payment.usertoken?.toLowerCase() === user.id.toString().toLowerCase())
            .map(payment => ({
              itemCategory: payment.item,
              credit: Number(payment.amount),
              createdAt: payment.created_at,
              fullname: payment.name,
              isExpense: true,
              type: 'expensespayment',
              id: payment.pay_id,
              memo: payment.memo,
            }));

          const orderPayments = orderspaymentResponse.data
            .filter(payment => payment.usertoken?.toLowerCase() === user.id.toString().toLowerCase())
            .map(payment => ({
              itemCategory: 'Accounts Receivable',
              debit: Number(payment.amount),
              createdAt: payment.created_at,
              fullname: payment.name,
              isExpense: false,
              type: 'order',
              id: payment.pay_id,
              memo: payment.memo,
            }));

          const returnPayment = returnpaymentResponse.data
            .filter(damages =>
              damages.usertoken.toLowerCase() === user.id.toString().toLowerCase() &&
              damages.type === 'money back'
            )
            .map(damages => {
              const totalAmount = damages.items.reduce(
                (sum, item) => sum + Number(item.quantity) * Number(item.rate),
                0
              );
              return {
                itemCategory: 'Refund',
                credit: totalAmount,
                createdAt: damages.created_at,
                fullname: damages.fullname,
                isExpense: false,
                type: 'return',
                id: damages.pay_id,
              };
            });

          const investmentPayments = investmentpaymentResponse.data.investment
            .filter(investment =>
              investment.usertoken?.toLowerCase() === user.id.toString().toLowerCase() &&
              investment.to === 'bank'
            )
            .map(investment => ({
              itemCategory: 'Investment Received',
              debit: Number(investment.amount),
              createdAt: investment.created_at,
              fullname: investment.person,
              isExpense: false,
              type: 'investment',
              id: investment.pay_id,
              memo: investment.memo || "",
            }));

          const investmentPaymentsPay = investmentpaymentResponse.data.investment
            .filter(investment =>
              investment.usertoken?.toLowerCase() === user.id.toString().toLowerCase() &&
              investment.from === 'bank'
            )
            .map(investment => ({
              itemCategory: 'Investment Payout',
              credit: Number(investment.amount),
              createdAt: investment.created_at,
              fullname: investment.person,
              isExpense: false,
              type: 'investment',
              id: investment.pay_id,
              memo: investment.memo || "",
            }));

          const combined = [
            ...userExpenses,
            ...userOrders,
            ...userOrders2,
            ...expensesPayments,
            ...orderPayments,
            ...returnPayment,
            ...investmentPayments,
            ...investmentPaymentsPay,
          ];

          combined.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
          setCombinedData(combined);
        }
      } catch (error) {
        if (error.response?.status === 429) {
          // Rate limit error
        } else if (error.response?.status >= 500) {
          // Server error
        } else {
          // General error
        }
      } finally {
        setLoading(false);
      }
    };

    if (user.id) {
      fetchData();
    }
  }, [user.id]);


  // Filter data by month
  {/**useEffect(() => {
    if (combinedData.length > 0) {
      const filtered = combinedData.filter(item => {
        if (!selectedMonth) {
          return true;
        }
        const month = new Date(item.createdAt).toLocaleDateString('en-US', { month: '2-digit' });
        return month === selectedMonth;
      });
      setFilteredData(filtered);
      setPage(0); // Reset page when filter changes
    } else {
      setFilteredData([]);
    }
  }, [combinedData, selectedMonth]);*/}

  useEffect(() => {
    if (combinedData.length > 0) {
      const filtered = combinedData.filter(item => {
        const date = new Date(item.createdAt);
        const itemMonth = date.toLocaleDateString('en-US', { month: '2-digit' });
        const itemYear = date.getFullYear().toString();

        const matchMonth = selectedMonth ? itemMonth === selectedMonth : true;
        const matchYear = selectedYear ? itemYear === selectedYear : true;

        return matchMonth && matchYear;
      });

      setFilteredData(filtered);
      setPage(0); // Reset page when filter changes
    } else {
      setFilteredData([]);
    }
  }, [combinedData, selectedMonth, selectedYear]);


  // Handle page change
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handle rows per page change
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');

    const sortedData = [...filteredData].sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    let runningBalance = 0;
    let totalCredit = 0;
    let totalDebit = 0;

    const rowsHtml = sortedData.map(item => {
      const debit = item.debit || 0;
      const credit = item.credit || 0;

      totalDebit += debit;
      totalCredit += credit;
      runningBalance += debit - credit;

      return `
        <tr>
          <td>${formatDate(item.createdAt)}</td>
          <td>${item.id}</td>
          <td>${item.type === 'order' ? 'Payment' :
          item.type === 'expenses' ? 'Expense' :
            item.type === 'return' ? 'Return' :
              item.type === 'investment' ? 'Investment' :
                item.type === 'expensespayment' ? 'Expense' :
                  item.type}</td>
          <td>${item.fullname || ''}<br />${item.itemCategory || ''}</td>
          <td>${credit ? credit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
          <td>${debit ? debit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}</td>
          <td>${runningBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
        </tr>
      `;
    }).join('');

    const summaryTitle = selectedMonth ? 'Closing Balance' : 'Ending Balance';

    const summaryHtml = `
      <div style="float: right; text-align: left; margin-bottom: 20px;">
        <table style="border: none;">
          <tr><td><strong>Total Payment:</strong></td><td>${totalCredit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>
          <tr><td><strong>Total Deposit:</strong></td><td>${totalDebit.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>
          <tr><td><strong>${summaryTitle}:</strong></td><td>${runningBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td></tr>
        </table>
      </div>
    `;

    const headerHtml = `
      <div style="float: left; text-align: left;">
        <h2 style="margin: 4px 0; text-transform: uppercase">${user.companyname || ''}</h2>
        <p style="margin: 0;"><strong>Bank Account History</strong></p>
        <p style="margin: 0;">
          ${selectedMonth
        ? months.find((month) => month.value === selectedMonth)?.label
        : 'All Month'
      } - ${new Date().getFullYear()}
        </p>
        <p style="margin: 0;">Currency - LKR</p>
      </div>
    `;

    printWindow.document.write(`
      <html>
        <head>
          <title>Bank Account History</title>
          <style>
            table {
              width: 100%;
              border-collapse: collapse;
            }
            th, td {
              padding: 8px;
              text-align: left;
            }
            th {
              background-color: rgb(175, 175, 175);
            }
          </style>
        </head>
        <body>
          <div style="overflow: hidden; margin-bottom: 20px; border-bottom: 1px solid black">
            ${headerHtml}
            ${summaryHtml}
          </div>
  
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Ref. no</th>
                <th>Type</th>
                <th>Description</th>
                <th>Payment</th>
                <th>Deposit</th>
                <th>Balance</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.print();
  };

  const displayedData = filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleBackClick = () => {
    navigate("/allreport");
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  const getDisplayedRowsWithBalance = () => {
    const sortedData = [...displayedData].sort(
      (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
    );

    let balance = 0;
    const rowsWithBalance = sortedData.map((item) => {
      const debit = item.debit || 0;
      const credit = item.credit || 0;
      balance += debit - credit;
      return { ...item, balance };
    });

    // Show newest entries first
    return rowsWithBalance.reverse();
  };

  const dataWithBalance = getDisplayedRowsWithBalance();

  const handleExport = () => {
    const exportData = dataWithBalance.map(item => ({
      'Date': formatDate(item.createdAt),
      'Ref. No': `#${item.id}`,
      'Type':
        item.type === 'order' ? 'Payment' :
          item.type === 'expenses' ? 'Expense' :
            item.type === 'return' ? 'Return' :
              item.type === 'investment' ? 'Investment' :
                item.type === 'expensespayment' ? 'Expense' :
                  item.type,
      'Description': `${item.fullname || ''} ${item.itemCategory || ''}`.trim(),
      'Memo': item.memo || '',
      'Payment (LKR)': item.credit ? Number(item.credit).toFixed(2) : '-',
      'Deposit (LKR)': item.debit ? Number(item.debit).toFixed(2) : '-',
      'Balance (LKR)': item.balance ? Number(item.balance).toFixed(2) : '-',
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Transactions');

    XLSX.writeFile(workbook, 'Transaction_Report.xlsx');
  };


  return (
    <Box
      sx={{ mt: 10 }}
    >
      <AnimatedTypography
        component="h1"
        sx={{
          ml: { xs: 2, sm: 3 }, // Adjust margins for responsiveness
          mt: { xs: 2, sm: 3 },
          color: 'secondary.main',
          letterSpacing: '0.01em',
          fontSize: { xs: '20px', sm: '24px' }, // Responsive font size
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
          {/**<Typography
            component="span"
            sx={{
              display: { xs: 'none', sm: 'inline' },
              fontSize: { xs: '14px', sm: '20px' },
              fontWeight: 'bold',
              color: '',
            }}
          >
            Bank Account History
          </Typography>
          <Typography
            component="span"
            sx={{
              display: { xs: 'none', sm: 'inline' }, // Hidden on xs
              ml: 1,
              fontSize: '14px',
              color: '',
            }}
          >
            |
          </Typography>*/}
          <HomeIcon
            sx={{
              ml: 1,
              fontSize: '18px',
              color: '#6d7081',
            }}
          />
          <Typography
            component="span"
            sx={{
              ml: 0.5,
              fontSize: '14px',
              color: '#6d7081',
            }}
          >
            - Report - Bank Account History
          </Typography>
        </Box>
      </AnimatedTypography>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width:  {sm:'95%',xs:'90%'},
          maxWidth: 'auto',
          margin: 'auto',
          padding: '10px',
          bgcolor: 'background.paper',
          border: '1px solid #EDF2F7',
          borderRadius: '16px',
          marginTop: '30px',
          position: 'relative',
          '@media (max-width: 600px)': {
            marginTop: '10px',
          },
        }}>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'left',
              '@media (max-width: 600px)': {
                margin: 'auto',
              },

            }}
          >
            <Box sx={{ color: '#1e293b' }}><h2>Bank Account History</h2></Box>
            <Box
              component="div"
              sx={{
                mb: { sm: 2, xs: 'none' },
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                color: 'primary.main',
                width: 'fit-content',
                '&:hover': {
                  color: 'primary.main',
                }
              }}
              onClick={handleBackClick}
            >
              <ArrowBackIcon fontSize="small" />
              <Typography
                variant="body2"
                sx={{
                  ml: 0.5,
                  fontWeight: 500
                }}
              >
                Back to Report
              </Typography>
            </Box>
          </Box>

          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: isSearchOpen ? 1 : 0, height: isSearchOpen ? 'auto' : 0 }}
            transition={{ duration: 0.3 }}
            style={{ overflow: 'hidden', }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 1,
                width: '80%',
                alignItems: 'center',
                '@media (max-width: 600px)': {
                  margin: 'auto',
                },
              }}
            >
              <Select
                value={selectedMonth}
                displayEmpty
                size="small"
                onChange={handleMonthChange}
                sx={{
                  width: '250px',
                  height: '40px',
                  mt: 1,
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              >
                <MenuItem value="">All Months</MenuItem>
                {months.map((month) => (
                  <MenuItem key={month.value} value={month.value}>
                    {month.label}
                  </MenuItem>
                ))}
              </Select>
              <Select
                value={selectedYear}
                displayEmpty
                size="small"
                onChange={(e) => setSelectedYear(e.target.value)}
                sx={{
                  width: '245px',
                  height: '40px',
                  mt: 0,
                  bgcolor: 'rgba(240, 240, 240, 0.17)',
                  borderRadius: '8px',
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                  },
                }}
              >
                <MenuItem value="">All Years</MenuItem>
                {yearOptions.map((year) => (
                  <MenuItem key={year} value={year.toString()}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </motion.div>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              mt: { xs: 2, sm: 0 },
              justifyContent: { xs: 'center', sm: 'flex-end' },
              width: { xs: '100%', sm: 'auto' },
            }}
          >
            <IconButton
              onClick={toggleSearch}
              sx={{
                display: { sm: 'none', xs: 'flex' }
              }}
            >
              <SearchIcon />
            </IconButton>

            <IconButton onClick={handlePrint} aria-label="print"
              sx={{
                color: 'black'
              }}
            >
              <PrintIcon />
            </IconButton>

          </Box>
        </Box>


        <Box
          sx={{
            display: { sm: 'flex', xs: 'none' },
            flexDirection: 'row',
            width: '100%',
            height: '50px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20%',
            }}
          >
            <Select
              value={selectedMonth}
              displayEmpty
              size="small"
              onChange={handleMonthChange}
              sx={{
                width: '245px',
                height: '40px',
                mt: 0,
                bgcolor: 'rgba(240, 240, 240, 0.17)',
                borderRadius: '8px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            >
              <MenuItem value="">All Months</MenuItem>
              {months.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '20%',
            }}
          >
            <Select
              value={selectedYear}
              displayEmpty
              size="small"
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{
                width: '245px',
                height: '40px',
                mt: 0,
                bgcolor: 'rgba(240, 240, 240, 0.17)',
                borderRadius: '8px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                },
              }}
            >
              <MenuItem value="">All Years</MenuItem>
              {yearOptions.map((year) => (
                <MenuItem key={year} value={year.toString()}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'right',
              justifyContent: 'right',
              width: '60%',
              gap: 1
            }}
          >
            <Button
              onClick={handleExport}
              sx={{
                mb: 2,
                border: '1px solid rgba(129, 129, 129, 0.14)',
                borderRadius: '8px',
                boxShadow: 'none',
                color: 'text.secondary',
                backgroundColor: 'rgba(240, 240, 240, 0.14)',
                '&:hover': {
                  backgroundColor: 'black',
                  color: 'white',
                },
              }}
            >
              Export to xlsx
            </Button>
          </Box>
        </Box>

        <Box sx={{ width: '100%', mb: 2, overflow: 'hidden' }}>
          <TableContainer style={{ marginTop: '20px' }}>
            <Table stickyHeader aria-label="combined data table" sx={{ p: 2 }}>
              <TableHead>
                <TableRow sx={{ '& td, & th': { padding: '8px 12px', bgcolor: '#F7FAFC' } }}>
                  <TableCell sx={{ fontWeight: 'bold', }}>Transaction ID/Date</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', }}>Description</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', }}>Memo</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', }} align="right">Payment (LKR)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', }} align="right">Deposit (LKR)</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', }} align="right">Balance (LKR)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow sx={{ '& td, & th': { padding: '8px 12px' } }}>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography variant="body2" sx={{ mt: 2 }}>
                        Loading...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography variant="body1">No data found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  dataWithBalance.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                      <span style={{color:'rgba(31, 30, 30, 1)'}}>{item.id}</span><br/>
                        {formatDate(item.createdAt)}
                      </TableCell>
                      
                      <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                        {item.type === 'order' ? 'Payment' :
                          item.type === 'expense' ? 'Expense' :
                            item.type === 'return' ? 'Return' :
                              item.type === 'investment' ? 'Investment' :
                                item.type === 'expensespayment' ? 'Expense' :
                                  item.type}
                      </TableCell>

                      <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                        {item.fullname}
                        <br />
                        {item.itemCategory}
                      </TableCell>
                      <TableCell sx={{ color: '#636363', fontSize: '14px' }}>
                        {item.memo}
                      </TableCell>
                      <TableCell sx={{ color: '#636363', fontSize: '14px' }} align="right">
                        {item.credit ? Number(item.credit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                      </TableCell>
                      <TableCell sx={{ color: '#636363', fontSize: '14px' }} align="right">
                        {item.debit ? Number(item.debit).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : ''}
                      </TableCell>
                      <TableCell
                        sx={{
                          color: Number(item.balance) < 0 ? 'red' : 'text.secondary',
                          fontWeight: 'bold',
                          fontSize: '14px'
                        }}
                        align="right"
                      >
                        {Number(item.balance).toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            display="flex"
            justifyContent="center"
            width="100%"
            mt={2}
          >
            {/**<TablePagination
              rowsPerPageOptions={[100, 200, 300]}
              component="div"
              count={filteredData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage=""
            />*/}
          </Box>
        </Box>
      </Box>
      {loading && (
        <Box sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'white',
          zIndex: 9999
        }}>
          <l-trio
            size="50"
            speed="1.3"
            color="red"
          ></l-trio>
        </Box>
      )}
      <ScrollToTopButton />
    </Box>
  );
};

export default CombinedTable;